// Строительный кран — low-poly, фирменные цвета Этажи, логотип на кабине.
// Трос — верлет-цепочка: провисает, отстаёт от блока при качании и выпрямляется
// на пике амплитуды. Именно это делает маятник «живым», а не палкой.
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { COLORS } from './scene.mjs';

const ROPE_POINTS = 9;      // узлов в цепочке
const ROPE_ITERATIONS = 4;  // проходов решателя длины за кадр
const ROPE_GRAVITY = 9;

// Рисуем логотип «ЭТАЖИ» на канвасе — дешевле, чем грузить шрифтовую геометрию.
// mirrored — для граней -x/-z, где UV бокса иначе отдают текст задом наперёд.
function makeLogoTexture(mirrored = false) {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 128;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#e2231a';
  ctx.fillRect(0, 0, 256, 128);
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 52px Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  if (mirrored) {
    ctx.translate(256, 0);
    ctx.scale(-1, 1);
  }
  ctx.fillText('ЭТАЖИ', 128, 66);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/**
 * Собирает кран. Мачта стоит сбоку, стрела нависает над стройплощадкой (x=0).
 */
export function createCrane() {
  const group = new THREE.Group();

  const steelMat = new THREE.MeshLambertMaterial({ color: COLORS.steel });
  const darkMat = new THREE.MeshLambertMaterial({ color: COLORS.dark });
  const redMat = new THREE.MeshLambertMaterial({ color: COLORS.red });

  const mastX = -7;     // где стоит мачта
  const jibY = 11;      // высота стрелы
  const trolleyX = 0;   // тележка ровно над площадкой

  // Мачта — решётку имитируем чередованием секций. Кран поднимается вместе
  // с башней целиком, поэтому мачта продолжается ВНИЗ на высоту кадра —
  // иначе при подъёме видно, что она висит в воздухе без основания.
  const MAST_BELOW = 20; // юнитов вниз — за нижний край кадра при любом зуме
  const redSegs = [];
  const steelSegs = [];
  const segCount = Math.floor((jibY + MAST_BELOW) / 1.6);
  for (let i = 0; i < segCount; i++) {
    const seg = new THREE.BoxGeometry(0.7, 1.4, 0.7);
    seg.translate(mastX, 0.8 + i * 1.6 - MAST_BELOW, 0);
    (i % 2 ? steelSegs : redSegs).push(seg);
  }
  // Секции статичны относительно крана — мержим в два меша вместо ~20
  for (const [geoms, mat] of [[redSegs, redMat], [steelSegs, steelMat]]) {
    const mesh = new THREE.Mesh(mergeGeometries(geoms), mat);
    mesh.castShadow = true;
    group.add(mesh);
    for (const geom of geoms) geom.dispose();
  }

  const top = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), darkMat);
  top.position.set(mastX, jibY + 0.5, 0);
  group.add(top);

  // Логотип на всех четырёх боках кабины: виден и в облёте, и с любого ракурса.
  // Порядок граней BoxGeometry: +x, -x, +y, -y, +z, -z; на -x и -z UV идут
  // в обратную сторону — им отдаём зеркальную текстуру, чтобы текст читался.
  const logoMat = new THREE.MeshLambertMaterial({ map: makeLogoTexture() });
  const logoMatMirror = new THREE.MeshLambertMaterial({ map: makeLogoTexture(true) });
  const cabinMats = [
    logoMat,        // +x — к камере справа
    logoMatMirror,  // -x
    redMat, redMat, // крыша и низ
    logoMat,        // +z — фронт
    logoMatMirror,  // -z
  ];
  const cabin = new THREE.Mesh(new THREE.BoxGeometry(1.5, 1.1, 1.2), cabinMats);
  cabin.position.set(mastX + 1, jibY - 0.4, 0.15);
  cabin.castShadow = true;
  group.add(cabin);

  // Глаза-фары: кран как персонаж — моргает, щурится при неудаче
  const eyeMat = new THREE.MeshBasicMaterial({ color: 0xfff6d0 });
  const eyes = [];
  for (const dx of [-0.32, 0.32]) {
    const eye = new THREE.Mesh(new THREE.CircleGeometry(0.14, 12), eyeMat);
    eye.position.set(cabin.position.x + dx, cabin.position.y + 0.12, cabin.position.z + 0.62);
    group.add(eye);
    eyes.push(eye);
  }
  const pupilMat = new THREE.MeshBasicMaterial({ color: COLORS.dark });
  const pupils = [];
  for (const dx of [-0.32, 0.32]) {
    const p = new THREE.Mesh(new THREE.CircleGeometry(0.06, 10), pupilMat);
    p.position.set(cabin.position.x + dx, cabin.position.y + 0.12, cabin.position.z + 0.63);
    group.add(p);
    pupils.push(p);
  }

  // Стрела в сторону площадки + противовесная консоль
  const jib = new THREE.Mesh(new THREE.BoxGeometry(11, 0.5, 0.5), redMat);
  jib.position.set(mastX + 5.5, jibY + 0.2, 0);
  jib.castShadow = true;
  group.add(jib);

  const counterJib = new THREE.Mesh(new THREE.BoxGeometry(3.5, 0.5, 0.5), redMat);
  counterJib.position.set(mastX - 1.75, jibY + 0.2, 0);
  group.add(counterJib);

  const counterWeight = new THREE.Mesh(new THREE.BoxGeometry(1, 1.2, 1.4), darkMat);
  counterWeight.position.set(mastX - 3.2, jibY - 0.3, 0);
  counterWeight.castShadow = true;
  group.add(counterWeight);

  const ties = new THREE.LineSegments(
    new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(mastX, jibY + 1, 0), new THREE.Vector3(mastX + 10.5, jibY + 0.4, 0),
      new THREE.Vector3(mastX, jibY + 1, 0), new THREE.Vector3(mastX - 3.2, jibY + 0.4, 0),
    ]),
    new THREE.LineBasicMaterial({ color: COLORS.dark })
  );
  group.add(ties);

  const trolley = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.35, 0.8), darkMat);
  trolley.position.set(trolleyX, jibY - 0.15, 0);
  group.add(trolley);

  // Флаг Этажей на противовесе — колышется синус-волной по вершинам
  const flagGeom = new THREE.PlaneGeometry(1.2, 0.7, 8, 3);
  const flag = new THREE.Mesh(
    flagGeom,
    new THREE.MeshLambertMaterial({ color: COLORS.red, side: THREE.DoubleSide })
  );
  flag.position.set(mastX - 4.3, jibY + 1.2, 0);
  group.add(flag);
  const flagPole = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 1.6, 5), darkMat);
  flagPole.position.set(mastX - 3.85, jibY + 1.1, 0);
  group.add(flagPole);
  const flagBase = flagGeom.attributes.position.array.slice();

  // --- Трос: верлет-цепочка + сегменты-цилиндры ---
  const ropeMat = new THREE.MeshLambertMaterial({ color: 0x3a3a3a });
  const segGeom = new THREE.CylinderGeometry(0.032, 0.032, 1, 5);
  const ropeSegments = [];
  for (let i = 0; i < ROPE_POINTS - 1; i++) {
    const seg = new THREE.Mesh(segGeom, ropeMat);
    seg.castShadow = true;
    group.add(seg);
    ropeSegments.push(seg);
  }
  const nodes = [];
  for (let i = 0; i < ROPE_POINTS; i++) {
    const p = new THREE.Vector3(trolleyX, jibY - 0.15 - i * 0.4, 0);
    nodes.push({ pos: p.clone(), prev: p.clone() });
  }
  const UP = new THREE.Vector3(0, 1, 0);
  const tmpDir = new THREE.Vector3();
  const tmpStart = new THREE.Vector3();

  function setRopeVisible(v) {
    for (const s of ropeSegments) s.visible = v;
  }

  /**
   * Обновляет верёвку. attachWorld — точка крепления (верх блока) в мировых
   * координатах; трос живёт внутри группы крана, поэтому переводим в локальные.
   */
  function updateRope(dt, attachWorld, visible = true) {
    setRopeVisible(visible);
    if (!visible) return;

    const anchor = tmpStart.set(trolleyX, jibY - 0.15, 0);
    const attach = group.worldToLocal(attachWorld.clone());

    // Верлет-интеграция свободных узлов
    const step = Math.min(dt, 1 / 60);
    for (let i = 1; i < nodes.length - 1; i++) {
      const n = nodes[i];
      const vx = (n.pos.x - n.prev.x) * 0.96;
      const vy = (n.pos.y - n.prev.y) * 0.96;
      const vz = (n.pos.z - n.prev.z) * 0.96;
      n.prev.copy(n.pos);
      n.pos.x += vx;
      n.pos.y += vy - ROPE_GRAVITY * step * step;
      n.pos.z += vz;
    }
    // Концы прибиты: верх к тележке, низ к блоку
    nodes[0].pos.copy(anchor);
    nodes[nodes.length - 1].pos.copy(attach);

    // Решатель длины: сегмент не растягивается сильнее нужного
    const segLen = anchor.distanceTo(attach) / (ROPE_POINTS - 1);
    for (let it = 0; it < ROPE_ITERATIONS; it++) {
      for (let i = 0; i < nodes.length - 1; i++) {
        const a = nodes[i].pos;
        const b = nodes[i + 1].pos;
        tmpDir.subVectors(b, a);
        const d = tmpDir.length() || 1e-6;
        const diff = (d - segLen) / d * 0.5;
        const ox = tmpDir.x * diff, oy = tmpDir.y * diff, oz = tmpDir.z * diff;
        if (i !== 0) { a.x += ox; a.y += oy; a.z += oz; }
        if (i + 1 !== nodes.length - 1) { b.x -= ox; b.y -= oy; b.z -= oz; }
      }
      nodes[0].pos.copy(anchor);
      nodes[nodes.length - 1].pos.copy(attach);
    }

    // Рендер сегментов между узлами
    for (let i = 0; i < ropeSegments.length; i++) {
      const a = nodes[i].pos;
      const b = nodes[i + 1].pos;
      tmpDir.subVectors(b, a);
      const len = Math.max(0.001, tmpDir.length());
      const seg = ropeSegments[i];
      seg.position.copy(a).addScaledVector(tmpDir, 0.5);
      seg.scale.set(1, len, 1);
      seg.quaternion.setFromUnitVectors(UP, tmpDir.normalize());
    }
  }

  // Сброс верёвки в прямое положение (смена уровня, новый блок)
  function resetRope(attachWorld) {
    const anchor = new THREE.Vector3(trolleyX, jibY - 0.15, 0);
    const attach = attachWorld ? group.worldToLocal(attachWorld.clone()) : anchor.clone().setY(anchor.y - 3.2);
    for (let i = 0; i < nodes.length; i++) {
      const t = i / (nodes.length - 1);
      nodes[i].pos.lerpVectors(anchor, attach, t);
      nodes[i].prev.copy(nodes[i].pos);
    }
  }

  // Мимика крана: моргание, «грусть» при промахе, «радость» на комбо
  let blinkTimer = 2 + Math.random() * 3;
  let mood = 0;      // -1 грусть, 0 норма, +1 радость
  let moodTimer = 0;
  function setMood(m, seconds = 1.2) { mood = m; moodTimer = seconds; }

  // Лёгкое «дыхание» крана + мимика + флаг
  function update(dt, t) {
    group.rotation.z = Math.sin(t * 0.6) * 0.004;

    blinkTimer -= dt;
    const blinking = blinkTimer < 0.12;
    if (blinkTimer <= 0) blinkTimer = 2.5 + Math.random() * 3.5;
    if (moodTimer > 0) moodTimer -= dt; else mood = 0;

    for (let i = 0; i < eyes.length; i++) {
      eyes[i].scale.y = blinking ? 0.12 : (mood < 0 ? 0.65 : 1);
      pupils[i].position.y = eyes[i].position.y + (mood > 0 ? 0.03 : mood < 0 ? -0.03 : 0);
      pupils[i].visible = !blinking;
    }

    // Волна по флагу
    const arr = flagGeom.attributes.position.array;
    for (let i = 0; i < arr.length; i += 3) {
      const x = flagBase[i];
      arr[i + 2] = Math.sin(t * 6 + x * 4) * 0.09 * (x + 0.6);
    }
    flagGeom.attributes.position.needsUpdate = true;
  }

  return { group, jibY, trolleyX, updateRope, resetRope, update, setMood };
}
